import React, { useState } from 'react';
import '../styles/PiscineCard.css';
import { heureVersMinutes, estOuvertA, creneauCouvrant, minutesVersHeure, formaterHoraireAffichage } from '../utils/horaires';
import CinemasSection from './CinemasSection';

// Une piscine appartient au réseau tarifaire partagé "Abonnement Piscines de
// Paris" quand elle propose la ligne tarifaire "Abonnement de 3 mois" à son
// prix standard de 43€ (vérifié sur l'ensemble des piscines: soit cette
// ligne est absente — grands équipements/complexes à tarification propre —
// soit elle vaut exactement 43€ partout où le réseau s'applique).
function estDansLeForfaitPiscinesDeParis(tarifs) {
  if (!tarifs) return false;
  return tarifs.some(t => {
    if (t.categorie.trim().toLowerCase() !== 'abonnement de 3 mois') return false;
    const m = t.prix.match(/(\d+(?:[.,]\d+)?)/);
    return m && parseFloat(m[1].replace(',', '.')) === 43;
  });
}

function PiscineCard({ piscine, userLocation, desiredTime, jourLabel, selectedDate }) {
  const [expanded, setExpanded] = useState(false);
  const [cinemasOpen, setCinemasOpen] = useState(false);
  const [tarifsOpen, setTarifsOpen] = useState(false);

  const openMapsDirections = (mode) => {
    const modeParam = {
      'marche': 'walking',
      'velo': 'bicycling',
      'transports': 'transit'
    }[mode] || 'driving';

    // On cherche la destination par nom plutôt que par coordonnées brutes:
    // un pin de coordonnées se fait souvent ré-étiqueter par Google Maps
    // avec le nom d'un commerce voisin (boutique, Airbnb...) présent au
    // même point exact, même quand nos coordonnées sont correctes.
    const origin = `${userLocation.latitude},${userLocation.longitude}`;
    const destination = encodeURIComponent(`${piscine.nom}, Paris`);
    const url = `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destination}&travelmode=${modeParam}`;
    window.open(url, '_blank');
  };

  const horaireJour = piscine.horaireJour || piscine.horaireAujourdhui || 'Horaires non disponibles';

  const heureMinutes = heureVersMinutes(desiredTime);
  const ouvert = estOuvertA(horaireJour, heureMinutes);
  const creneau = ouvert ? creneauCouvrant(horaireJour, heureMinutes) : null;

  const actionButtons = (
    <div className="action-buttons">
      <button className="btn-action btn-walk" onClick={() => openMapsDirections('marche')} title="À pied">
        🚶
      </button>
      <button className="btn-action btn-bike" onClick={() => openMapsDirections('velo')} title="À vélo">
        🚴
      </button>
      <button className="btn-action btn-transit" onClick={() => openMapsDirections('transports')} title="Transports">
        🚇
      </button>
    </div>
  );

  const detailFields = (
    <>
      <div className="info-row">
        <span className="label">📍 Arrondissement</span>
        <span className="value">{piscine.arrondissement}</span>
      </div>

      <div className="info-row">
        <span className="label">📬 Adresse</span>
        <span className="value">{piscine.adresse}</span>
      </div>

      {piscine.bassins && piscine.bassins.length > 0 && (
        <div className="info-row">
          <span className="label">📏 Bassins</span>
          <span className="value">
            {piscine.bassins.map(b => `${b.nom} : ${b.longueur}`).join(' · ')}
          </span>
        </div>
      )}

      {piscine.tarifs && piscine.tarifs.length > 0 && (
        <div className="tarifs-section">
          <button className="tarifs-toggle-btn" onClick={() => setTarifsOpen(!tarifsOpen)}>
            <span>💶 Tarifs</span>
            <span>{tarifsOpen ? '▼' : '▶'}</span>
          </button>
          {tarifsOpen && (
            <table className="tarifs-table">
              <tbody>
                {piscine.tarifs.map((t, i) => (
                  <tr key={i}>
                    <td className="tarifs-categorie">{t.categorie}</td>
                    <td className="tarifs-prix">{t.prix}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      <div className="metro-badge">
        {piscine.metro.map((m, i) => (
          <span key={i}>Ⓜ️ {m}</span>
        ))}
      </div>

      {piscine.lastUpdated && (
        <div className="horaire-line">
          <strong>Dernière mise à jour:</strong>{' '}
          {new Date(piscine.lastUpdated).toLocaleString('fr-FR', {
            day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit'
          })}
        </div>
      )}

      {piscine.cinemas && piscine.cinemas.length > 0 && (
        <div className="cinemas-header-section">
          <button className="cinemas-toggle-btn" onClick={() => setCinemasOpen(!cinemasOpen)}>
            <span>🎬 {piscine.cinemas.length} cinéma(s) à moins de 15 min — voir les séances</span>
            <span>{cinemasOpen ? '▼' : '▶'}</span>
          </button>
          {cinemasOpen && (
            <CinemasSection cinemas={piscine.cinemas} day={selectedDate} desiredTime={desiredTime} />
          )}
        </div>
      )}
    </>
  );

  // Piscine fermée: carte compacte sur une ligne, tout le reste replié.
  if (ouvert === false) {
    return (
      <div className="piscine-card piscine-card-compact">
        <button className="compact-row" onClick={() => setExpanded(!expanded)}>
          <span className="compact-nom">{piscine.nom}</span>
          <span className="distance compact-distance">{piscine.distance.toFixed(2)} km</span>
          <span className="statut-badge statut-ferme">❌ Fermé</span>
          <span className="compact-toggle">{expanded ? '▼' : '▶'}</span>
        </button>

        {expanded && (
          <div className="card-body compact-expanded">
            <div className="info-row">
              <span className="label">🕐 {jourLabel || "Aujourd'hui"}</span>
              <span className="value horaire">{formaterHoraireAffichage(horaireJour)}</span>
            </div>
            {detailFields}
            {actionButtons}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="piscine-card">
      <div className="card-header">
        <div className="card-header-titres">
          <h3>{piscine.nom}</h3>
          {estDansLeForfaitPiscinesDeParis(piscine.tarifs) && (
            <div className="sous-titre-forfait">Abonnement Piscines de Paris</div>
          )}
        </div>
        <span className="distance">{piscine.distance.toFixed(2)} km</span>
      </div>

      <div className="card-body">
        {ouvert === true && (
          <span className="statut-badge statut-ouvert">
            ✅ Ouvert{creneau ? ` jusqu'à ${minutesVersHeure(creneau.fin)}` : ''}
          </span>
        )}
        {ouvert === null && (
          <span className="statut-badge statut-incertain">
            ℹ️ Horaires à vérifier
          </span>
        )}

        <div className="info-row">
          <span className="label">🕐 {jourLabel || "Aujourd'hui"}</span>
          <span className="value horaire">{formaterHoraireAffichage(horaireJour)}</span>
        </div>

        {expanded && (
          <div className="expanded-content">
            {detailFields}
          </div>
        )}
      </div>

      <div className="card-footer">
        <button
          className="btn-expand"
          onClick={() => setExpanded(!expanded)}
        >
          {expanded ? '▼' : '▶'} {expanded ? 'Moins' : 'Plus'}
        </button>

        {actionButtons}
      </div>
    </div>
  );
}

export default PiscineCard;
